// =============================================================================
// features/sales/campaigns/wizard/steps/Step5Cadence.tsx — Etapa 5: janela
// semanal, tentativas e intervalo mínimo (F35-S08, doc 26 §6.6). Mapeia
// direto para `weeklyWindow`/`maxAttempts`/`minIntervalMinutes` do contrato
// `Campaign` — nenhum campo novo inventado.
// =============================================================================

import * as React from 'react';

import { Input } from '../../../../../components/ui/Input';
import { cn } from '../../../../../lib/cn';
import { WEEKDAY_LABEL, WEEKDAY_ORDER, validateWeeklyWindow } from '../cadence';
import { WizardStepShell } from '../components/WizardStepShell';
import type { CadenceValues } from '../types';

export interface Step5CadenceProps {
  values: CadenceValues;
  onChange: (values: CadenceValues) => void;
  onBack: () => void;
  onContinue: () => void;
  isPending: boolean;
  errorMessage: string | null;
}

export function Step5Cadence({
  values,
  onChange,
  onBack,
  onContinue,
  isPending,
  errorMessage,
}: Step5CadenceProps): React.JSX.Element {
  const [touched, setTouched] = React.useState(false);
  const windowErrors = validateWeeklyWindow(values.weeklyWindow);
  const hasErrors = Object.keys(windowErrors.byDay).length > 0;

  function toggleDay(day: (typeof WEEKDAY_ORDER)[number], enabled: boolean): void {
    const next = { ...values.weeklyWindow };
    if (enabled) {
      next[day] = { start: '08:00', end: '18:00' };
    } else {
      delete next[day];
    }
    onChange({ ...values, weeklyWindow: next });
  }

  function updateDayTime(
    day: (typeof WEEKDAY_ORDER)[number],
    field: 'start' | 'end',
    value: string,
  ): void {
    const current = values.weeklyWindow[day];
    if (!current) return;
    onChange({
      ...values,
      weeklyWindow: { ...values.weeklyWindow, [day]: { ...current, [field]: value } },
    });
  }

  function handleContinue(): void {
    setTouched(true);
    if (hasErrors) return;
    onContinue();
  }

  return (
    <WizardStepShell
      title="Cadência de contato"
      description="Quando os vendedores podem trabalhar essa carteira e com que frequência voltar a tentar."
      onBack={onBack}
      onContinue={handleContinue}
      isPending={isPending}
      errorMessage={errorMessage}
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-semibold text-ink mb-1">Janela semanal permitida</legend>
        {touched && windowErrors.empty && (
          <p className="font-sans text-xs text-warning">
            Nenhum dia configurado ainda — a campanha não pode ser ativada sem ao menos uma janela.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {WEEKDAY_ORDER.map((day) => {
            const dayWindow = values.weeklyWindow[day];
            const enabled = dayWindow !== undefined;
            const dayError = touched ? windowErrors.byDay[day] : undefined;
            return (
              <div
                key={day}
                className={cn(
                  'flex flex-wrap items-center gap-3 rounded-sm border px-4 py-3',
                  enabled ? 'border-border-strong' : 'border-border',
                )}
                style={{ background: enabled ? 'var(--surface-1)' : 'var(--surface-muted)' }}
              >
                <label className="flex items-center gap-2 min-w-[110px] font-sans text-sm font-medium text-ink cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => toggleDay(day, e.target.checked)}
                    className="w-4 h-4 accent-azul"
                  />
                  {WEEKDAY_LABEL[day]}
                </label>
                {enabled && dayWindow && (
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <input
                      type="time"
                      aria-label={`${WEEKDAY_LABEL[day]} — início`}
                      value={dayWindow.start}
                      onChange={(e) => updateDayTime(day, 'start', e.target.value)}
                      className="h-[36px] px-2 rounded-sm border border-border-strong bg-surface-1 font-mono text-sm text-ink"
                    />
                    <span className="text-ink-3 text-sm">até</span>
                    <input
                      type="time"
                      aria-label={`${WEEKDAY_LABEL[day]} — fim`}
                      value={dayWindow.end}
                      onChange={(e) => updateDayTime(day, 'end', e.target.value)}
                      className="h-[36px] px-2 rounded-sm border border-border-strong bg-surface-1 font-mono text-sm text-ink"
                    />
                  </div>
                )}
                {dayError && (
                  <span role="alert" className="text-xs text-danger w-full">
                    {dayError}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          id="wizard-cadence-max-attempts"
          type="number"
          label="Tentativas máximas por cliente"
          min={1}
          max={50}
          value={values.maxAttempts}
          onChange={(e) => onChange({ ...values, maxAttempts: Number(e.target.value) || 1 })}
        />
        <Input
          id="wizard-cadence-min-interval"
          type="number"
          label="Intervalo mínimo entre tentativas (minutos)"
          min={0}
          max={10080}
          value={values.minIntervalMinutes}
          onChange={(e) => onChange({ ...values, minIntervalMinutes: Number(e.target.value) || 0 })}
        />
      </div>
    </WizardStepShell>
  );
}
